// Thin, defensive wrapper over @telegram-apps/sdk (v3). All Telegram calls are
// guarded so the app also renders in a plain browser during development: when
// not running inside Telegram we fall back to VITE_DEV_INIT_DATA (if provided)
// and treat lifecycle calls as no-ops.
import {
  init,
  miniAppReady,
  expandViewport,
  mountBackButton,
  showBackButton,
  hideBackButton,
  onBackButtonClick,
  isBackButtonSupported,
  openTelegramLink as sdkOpenTelegramLink,
  retrieveRawInitData,
  retrieveLaunchParams,
} from "@telegram-apps/sdk";

let initialized = false;
let insideTelegram = false;
let rawInitData: string | undefined;
let chatId: number | undefined;
let photoUrl: string | undefined;

function devInitData(): string | undefined {
  const v = import.meta.env.VITE_DEV_INIT_DATA;
  return v && v.length > 0 ? v : undefined;
}

/**
 * Parse `chat.id` out of a `startapp` launch param of the form `chat_<id>`.
 * The bot's group `/start` Launch button encodes the chat id here because
 * Telegram does not pass `initData.chat` on group inline/direct-link launches.
 */
function chatIdFromStartParam(param: string | undefined): number | undefined {
  if (!param) return undefined;
  const match = /^chat_(-?\d+)$/.exec(param);
  if (!match) return undefined;
  const n = Number(match[1]);
  return Number.isFinite(n) ? n : undefined;
}

/** Parse `chat.id` out of a raw initData query string (dev fallback path). */
function chatIdFromRaw(raw: string | undefined): number | undefined {
  if (!raw) return undefined;
  try {
    const params = new URLSearchParams(raw);
    const chat = params.get("chat");
    if (!chat) return undefined;
    const parsed = JSON.parse(chat) as { id?: number };
    return typeof parsed.id === "number" ? parsed.id : undefined;
  } catch {
    return undefined;
  }
}

/** Parse the user's `photo_url` out of a raw initData query string (dev fallback). */
function photoUrlFromRaw(raw: string | undefined): string | undefined {
  if (!raw) return undefined;
  try {
    const params = new URLSearchParams(raw);
    const user = params.get("user");
    if (!user) return undefined;
    const parsed = JSON.parse(user) as { photo_url?: string };
    return typeof parsed.photo_url === "string" ? parsed.photo_url : undefined;
  } catch {
    return undefined;
  }
}

/**
 * Initialize the Telegram environment once. Safe to call in a normal browser —
 * it detects the absence of Telegram and switches to the dev fallback.
 */
export function initTelegram(): void {
  if (initialized) return;
  initialized = true;

  try {
    init();
    rawInitData = retrieveRawInitData();
    if (rawInitData) {
      insideTelegram = true;
      const lp = retrieveLaunchParams();
      chatId = lp.tgWebAppData?.chat?.id;
      // Group launches carry the chat id via startapp (chat_<id>) instead.
      if (chatId === undefined)
        chatId = chatIdFromStartParam(lp.tgWebAppStartParam);
      if (chatId === undefined) chatId = chatIdFromRaw(rawInitData);

      photoUrl =
        lp.tgWebAppData?.user?.photo_url ?? photoUrlFromRaw(rawInitData);

      // Lifecycle: signal ready + expand to full height. Guarded individually
      // because availability varies by Telegram client version.
      try {
        miniAppReady();
      } catch {
        /* older clients */
      }
      try {
        if (expandViewport.isAvailable()) expandViewport();
      } catch {
        /* ignore */
      }
      try {
        if (mountBackButton.isAvailable()) mountBackButton();
      } catch {
        /* ignore */
      }
    }
  } catch {
    insideTelegram = false;
  }

  // Dev fallback: outside Telegram, use VITE_DEV_INIT_DATA if present.
  if (!insideTelegram) {
    rawInitData = devInitData();
    chatId = chatIdFromRaw(rawInitData);
    photoUrl = photoUrlFromRaw(rawInitData);
  }
}

export function isInsideTelegram(): boolean {
  return insideTelegram;
}

/** Raw initData string to send to POST /auth/verify. */
export function getInitData(): string | undefined {
  return rawInitData;
}

/** chatId resolved from launch params (group launches only). */
export function getChatId(): number | undefined {
  return chatId;
}

/** The user's avatar URL from initData, if Telegram provided one. */
export function getUserPhotoUrl(): string | undefined {
  return photoUrl;
}

/**
 * Open a Telegram deep link — a t.me URL such as `https://t.me/<handle>` (join a
 * group) or `https://t.me/<bot>?startgroup=...` (add the bot to a group). Uses the
 * Telegram SDK inside the webview; falls back to `window.open` in a plain browser
 * so the DM picker's links still work during development.
 */
export function openTelegramLink(url: string): void {
  try {
    if (insideTelegram && sdkOpenTelegramLink.isAvailable()) {
      sdkOpenTelegramLink(url);
      return;
    }
  } catch {
    /* fall through to window.open */
  }
  try {
    window.open(url, "_blank", "noopener");
  } catch {
    /* ignore */
  }
}

/** Bot @username (without "@"), from VITE_BOT_USERNAME — used for group deep links. */
export function getBotUsername(): string | undefined {
  const v = import.meta.env.VITE_BOT_USERNAME ?? "raccaibot";
  return v && v.length > 0 ? v : undefined;
}

/**
 * Show the Telegram BackButton and register a click handler. Returns a cleanup
 * function that hides the button and unsubscribes. No-op outside Telegram.
 */
export function showBackButtonWith(onClick: () => void): () => void {
  if (!insideTelegram) return () => {};
  try {
    if (!isBackButtonSupported()) return () => {};
    if (mountBackButton.isAvailable()) mountBackButton();
    showBackButton();
    const off = onBackButtonClick(onClick);
    return () => {
      try {
        off();
        hideBackButton();
      } catch {
        /* ignore */
      }
    };
  } catch {
    return () => {};
  }
}
